/**
 * Mitarbeiter-Anträge (Employee Requests)
 * - Browse available request templates
 * - Submit new requests with dynamic forms
 * - Track status of own requests
 * - Approve/reject as manager
 */
import React, { useState, useEffect, useCallback } from 'react';
import { employeeRequestsAPI } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePickerDE } from '../components/ui/date-picker-de';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  GraduationCap, ShoppingCart, Plane, Home, Monitor, CreditCard,
  FileCheck, Key, FileText, Plus, Send, Clock, CheckCircle2,
  XCircle, AlertCircle, ChevronRight, Loader2, Eye, MessageSquare, Settings,
} from 'lucide-react';
import { RequestTemplateManager } from './RequestTemplateManager';

const ICON_MAP = {
  GraduationCap, ShoppingCart, Plane, Home, Monitor, CreditCard,
  FileCheck, Key, FileText, Settings,
};

const STATUS_CONFIG = {
  pending: { label: 'Offen', color: 'bg-gray-500', icon: Clock },
  in_review: { label: 'In Pruefung', color: 'bg-blue-500', icon: AlertCircle },
  approved: { label: 'Genehmigt', color: 'bg-green-500', icon: CheckCircle2 },
  rejected: { label: 'Abgelehnt', color: 'bg-red-500', icon: XCircle },
  cancelled: { label: 'Storniert', color: 'bg-gray-400', icon: XCircle },
};

const ROLE_LABELS = {
  standortleiter: 'Standortleiter/in',
  niederlassungsleiter: 'Niederlassungsleiter/in',
  prokurist: 'Prokurist/in',
  geschaeftsfuehrer: 'Geschaeftsfuehrer/in',
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return format(parseISO(d), 'dd.MM.yyyy HH:mm', { locale: de }); }
  catch { return d; }
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} text-white`} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3 mr-1" />{cfg.label}
    </Badge>
  );
};

// ========== SUBMIT DIALOG ==========
const SubmitDialog = ({ open, onClose, template, onSubmitted }) => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const canManageOrg = user?.role === 'admin' || (hasPermission && hasPermission('settings', 'edit'));
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (template) {
      const defaults = {};
      (template.fields || []).forEach(f => { defaults[f.name] = ''; });
      setFormData(defaults);
    }
  }, [template]);

  useEffect(() => {
    if (!open || !template?.id || !(template.approval_chain?.length > 0)) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    employeeRequestsAPI.approvalPreview(template.id)
      .then((res) => { if (!cancelled) setPreview(res.data); })
      .catch(() => { if (!cancelled) setPreview(null); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [open, template]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await employeeRequestsAPI.submit({ template_id: template.id, form_data: formData });
      toast.success('Antrag eingereicht');
      onSubmitted();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Einreichen');
    } finally {
      setSubmitting(false);
    }
  };

  if (!template) return null;
  const IconComp = ICON_MAP[template.icon] || FileText;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="submit-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IconComp className="h-5 w-5 text-primary" />
            {template.name}
          </DialogTitle>
          <DialogDescription>{template.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {(template.fields || []).map((field) => (
            <div key={field.name} className="space-y-1.5">
              <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
              {field.field_type === 'textarea' ? (
                <Textarea
                  placeholder={field.placeholder}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData(p => ({ ...p, [field.name]: e.target.value }))}
                  data-testid={`field-${field.name}`}
                />
              ) : field.field_type === 'select' ? (
                <Select
                  value={formData[field.name] || ''}
                  onValueChange={(v) => setFormData(p => ({ ...p, [field.name]: v }))}
                >
                  <SelectTrigger data-testid={`field-${field.name}`}>
                    <SelectValue placeholder="Bitte waehlen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(field.options || []).map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : field.field_type === 'date' ? (
                <DatePickerDE
                  value={formData[field.name] || ''}
                  onChange={(v) => setFormData(p => ({ ...p, [field.name]: v || '' }))}
                  placeholder={field.placeholder || 'TT.MM.JJJJ'}
                  testId={`field-${field.name}`}
                />
              ) : (
                <Input
                  type={field.field_type === 'currency' ? 'number' : field.field_type === 'number' ? 'number' : 'text'}
                  step={field.field_type === 'currency' ? '0.01' : undefined}
                  placeholder={field.placeholder}
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData(p => ({ ...p, [field.name]: e.target.value }))}
                  data-testid={`field-${field.name}`}
                />
              )}
            </div>
          ))}

          {template.approval_chain?.length > 0 && (
            <div className="rounded-lg bg-muted/50 p-3 space-y-2" data-testid="approval-chain-preview">
              <p className="text-xs font-medium text-muted-foreground">Dein Antrag geht an</p>
              {previewLoading ? (
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Genehmiger werden ermittelt…
                </p>
              ) : preview?.steps?.length > 0 ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {preview.steps.map((s, i) => (
                    <React.Fragment key={`${s.role}-${i}`}>
                      {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
                      <Badge
                        variant={s.approver_name ? 'secondary' : 'destructive'}
                        className="text-xs"
                        data-testid={`approval-step-${i}`}
                      >
                        {s.approver_name || 'Nicht zugewiesen'}
                        <span className="opacity-60 ml-1">· {s.role_label}</span>
                      </Badge>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-1 flex-wrap">
                  {template.approval_chain.map((role, i) => (
                    <React.Fragment key={role}>
                      {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                      <Badge variant="outline" className="text-xs">{ROLE_LABELS[role] || role}</Badge>
                    </React.Fragment>
                  ))}
                </div>
              )}
              {preview && !preview.all_resolved && (
                <div className="space-y-1" data-testid="approval-warning">
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    Für mindestens eine Stufe ist kein Vorgesetzter hinterlegt. Bitte an HR/Admin wenden – der Antrag wird sonst nicht automatisch zugestellt.
                  </p>
                  {canManageOrg && (
                    <button
                      type="button"
                      onClick={() => { onClose(); navigate('/organization'); }}
                      className="text-xs font-medium text-primary hover:underline inline-flex items-center gap-1"
                      data-testid="approval-fix-link"
                    >
                      Standort-/Niederlassungsleiter verwalten
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="btn-submit-request">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Antrag einreichen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========== DETAIL DIALOG ==========
const DetailDialog = ({ open, onClose, request, user, onUpdated }) => {
  const [comment, setComment] = useState('');
  const [deciding, setDeciding] = useState(false);

  if (!request) return null;

  const isRequester = request.requester_id === user?.id;
  const isAdmin = user?.role === 'admin';
  const currentStep = request.approval_steps?.[request.current_step];
  const canDecide = request.status === 'in_review' &&
    currentStep && (currentStep.approver_id === user?.id || isAdmin);
  const canCancel = (isRequester || isAdmin) &&
    ['pending', 'in_review'].includes(request.status);

  const handleDecision = async (decision) => {
    setDeciding(true);
    try {
      await employeeRequestsAPI.decide(request.id, { decision, comment });
      toast.success(decision === 'approved' ? 'Genehmigt' : 'Abgelehnt');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally {
      setDeciding(false);
    }
  };

  const handleCancel = async () => {
    try {
      await employeeRequestsAPI.cancel(request.id);
      toast.success('Antrag storniert');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="request-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {request.template_name}
            <StatusBadge status={request.status} />
          </DialogTitle>
          <DialogDescription>
            Von {request.requester_name} am {formatDate(request.created_at)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Form data */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Antragsdaten</h4>
            {(request.field_definitions || []).map((field) => (
              <div key={field.name} className="grid grid-cols-3 gap-2 text-sm">
                <span className="text-muted-foreground">{field.label}</span>
                <span className="col-span-2 font-medium">
                  {request.form_data?.[field.name] || '-'}
                  {field.field_type === 'currency' && request.form_data?.[field.name] ? ' EUR' : ''}
                </span>
              </div>
            ))}
          </div>

          {/* Approval chain */}
          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Genehmigungsverlauf</h4>
            <div className="space-y-2">
              {(request.approval_steps || []).map((step, i) => {
                const stepStatus = STATUS_CONFIG[step.status] || STATUS_CONFIG.pending;
                const StepIcon = stepStatus.icon;
                const isCurrent = i === request.current_step && request.status === 'in_review';
                return (
                  <div
                    key={`step-${step.role || step.approver_name}-${i}`}
                    className={`flex items-start gap-3 p-2 rounded-lg ${isCurrent ? 'bg-blue-50 border border-blue-200' : 'bg-muted/30'}`}
                  >
                    <StepIcon className={`h-4 w-4 mt-0.5 ${
                      step.status === 'approved' ? 'text-green-500' :
                      step.status === 'rejected' ? 'text-red-500' :
                      isCurrent ? 'text-blue-500' : 'text-muted-foreground'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{step.role_label}</p>
                      <p className="text-xs text-muted-foreground">{step.approver_name}</p>
                      {step.comment && (
                        <p className="text-xs mt-1 italic">"{step.comment}"</p>
                      )}
                      {step.decided_at && (
                        <p className="text-xs text-muted-foreground mt-0.5">{formatDate(step.decided_at)}</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {stepStatus.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Decision area */}
          {canDecide && (
            <div className="space-y-2 border-t pt-3">
              <Label>Kommentar (optional)</Label>
              <Textarea
                placeholder="Optionaler Kommentar zur Entscheidung..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                data-testid="approval-comment"
              />
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => handleDecision('approved')}
                  disabled={deciding}
                  data-testid="btn-approve"
                >
                  {deciding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  Genehmigen
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => handleDecision('rejected')}
                  disabled={deciding}
                  data-testid="btn-reject"
                >
                  {deciding ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <XCircle className="h-4 w-4 mr-1" />}
                  Ablehnen
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {canCancel && (
            <Button variant="outline" size="sm" onClick={handleCancel} data-testid="btn-cancel-request">
              Antrag stornieren
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Schliessen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ========== MAIN PAGE ==========
export default function EmployeeRequests() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('new');
  const [submitTemplate, setSubmitTemplate] = useState(null);
  const [detailRequest, setDetailRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, reqRes, pendRes] = await Promise.all([
        employeeRequestsAPI.getTemplates(),
        employeeRequestsAPI.getAll(),
        employeeRequestsAPI.getPending(),
      ]);
      setTemplates(tplRes.data || []);
      setRequests(reqRes.data || []);
      setPendingApprovals(pendRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-open detail if ?view= param
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId && requests.length) {
      const found = requests.find(r => r.id === viewId);
      if (found) {
        setDetailRequest(found);
        setActiveTab('my');
      }
    }
  }, [searchParams, requests]);

  const isAdmin = user?.role === 'admin';
  const myRequests = requests.filter(r => r.requester_id === user?.id);
  const filteredRequests = activeTab === 'my'
    ? (statusFilter === 'all' ? myRequests : myRequests.filter(r => r.status === statusFilter))
    : activeTab === 'pending'
    ? pendingApprovals
    : activeTab === 'all' && isAdmin
    ? (statusFilter === 'all' ? requests : requests.filter(r => r.status === statusFilter))
    : [];

  // Group templates by category
  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'Sonstiges';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4" data-testid="requests-loading">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="requests-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Antragstellung</h1>
            <p className="text-muted-foreground mt-1">Antraege stellen und verwalten</p>
          </div>
          {pendingApprovals.length > 0 && (
            <Badge className="bg-amber-500 text-white text-sm px-3 py-1" data-testid="pending-count">
              {pendingApprovals.length} zur Genehmigung
            </Badge>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="new" data-testid="tab-new">Neuer Antrag</TabsTrigger>
            <TabsTrigger value="my" data-testid="tab-my">
              Meine Antraege ({myRequests.length})
            </TabsTrigger>
            {pendingApprovals.length > 0 && (
              <TabsTrigger value="pending" data-testid="tab-pending">
                Genehmigungen ({pendingApprovals.length})
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="all" data-testid="tab-all">Alle Antraege</TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger value="templates" data-testid="tab-templates">
                <Settings className="h-3.5 w-3.5 mr-1" />Vorlagen
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {/* ===== NEW REQUEST TAB ===== */}
        {activeTab === 'new' && (
          <div className="space-y-6">
            {Object.entries(grouped).map(([category, tpls]) => (
              <div key={category}>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {tpls.map((tpl) => {
                    const IconComp = ICON_MAP[tpl.icon] || FileText;
                    return (
                      <Card
                        key={tpl.id}
                        className="cursor-pointer hover:shadow-md transition-shadow border-border/60 hover:border-primary/30"
                        onClick={() => setSubmitTemplate(tpl)}
                        data-testid={`template-card-${tpl.name.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        <CardContent className="p-4 flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <IconComp className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{tpl.name}</p>
                            <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                              {tpl.description}
                            </p>
                            <div className="flex items-center gap-1 mt-2 flex-wrap">
                              {(tpl.approval_chain || []).map((role, i) => (
                                <React.Fragment key={role}>
                                  {i > 0 && <ChevronRight className="h-2.5 w-2.5 text-muted-foreground" />}
                                  <span className="text-[10px] text-muted-foreground">
                                    {ROLE_LABELS[role] || role}
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== REQUESTS LIST (my / pending / all) ===== */}
        {(activeTab === 'my' || activeTab === 'pending' || activeTab === 'all') && (
          <div className="space-y-3">
            {(activeTab === 'my' || activeTab === 'all') && (
              <div className="flex gap-2">
                {['all', 'in_review', 'approved', 'rejected', 'cancelled'].map(s => (
                  <Button
                    key={s}
                    variant={statusFilter === s ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter(s)}
                    data-testid={`filter-${s}`}
                  >
                    {s === 'all' ? 'Alle' : STATUS_CONFIG[s]?.label || s}
                  </Button>
                ))}
              </div>
            )}

            {filteredRequests.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center text-muted-foreground">
                  Keine Antraege gefunden
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredRequests.map((req) => {
                  const IconComp = ICON_MAP[req.template_icon] || FileText;
                  return (
                    <Card
                      key={req.id}
                      className="cursor-pointer hover:shadow-sm transition-shadow"
                      onClick={() => setDetailRequest(req)}
                      data-testid={`request-card-${req.id}`}
                    >
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="p-2 rounded-lg bg-muted">
                          <IconComp className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{req.template_name}</p>
                            <StatusBadge status={req.status} />
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {req.requester_name} &middot; {formatDate(req.created_at)}
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {/* ===== TEMPLATES ADMIN TAB ===== */}
        {activeTab === 'templates' && isAdmin && (
          <RequestTemplateManager onTemplatesChanged={loadData} />
        )}
      </div>

      <SubmitDialog
        open={!!submitTemplate}
        onClose={() => setSubmitTemplate(null)}
        template={submitTemplate}
        onSubmitted={loadData}
      />

      <DetailDialog
        open={!!detailRequest}
        onClose={() => setDetailRequest(null)}
        request={detailRequest}
        user={user}
        onUpdated={loadData}
      />
    </DashboardLayout>
  );
}
